/**
 * Money — decimal strings outside, integer minor units inside.
 *
 * Every amount in this system crosses the wire as a string like `"58500.00"` and is
 * stored as `NUMERIC(14,2)`. `pg` hands NUMERIC back as a string, and this module is
 * the only place allowed to turn one into a number: arithmetic happens in integer
 * cents, and the result is formatted back to a string before it leaves.
 *
 * The rule exists because of what floats do to a shopping cart. `0.1 + 0.2` is
 * `0.30000000000000004`, and an order with twenty lines carries that error into a
 * grand total that disagrees with the sum of its own rows by a cent — which is exactly
 * the kind of discrepancy nobody can explain three months later.
 *
 * These functions THROW on malformed input rather than returning NaN. A total that
 * cannot be computed must fail loudly at the point of the bad value, not silently
 * produce `"NaN"` and get written to the database.
 */

import { MONEY_SCALE } from './constants.js';

const FACTOR = 10 ** MONEY_SCALE;

export type MoneyInput = string | number;

/** Parse a decimal string or number to integer minor units (cents). */
export function toCents(value: MoneyInput): number {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('ERR_INVALID_MONEY');
    return Math.round(value * FACTOR);
  }
  const trimmed = value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) throw new Error('ERR_INVALID_MONEY');

  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = ''] = unsigned.split('.');

  // Round the fractional part at MONEY_SCALE rather than truncating, so a price typed
  // as "1.005" becomes 1.01 and not 1.00.
  const padded = (fraction + '0'.repeat(MONEY_SCALE + 1)).slice(0, MONEY_SCALE + 1);
  const kept = Number(padded.slice(0, MONEY_SCALE));
  const next = Number(padded.slice(MONEY_SCALE, MONEY_SCALE + 1));
  const cents = Number(whole) * FACTOR + kept + (next >= 5 ? 1 : 0);

  return negative ? -cents : cents;
}

/** Format integer minor units back to the canonical `"0.00"` wire string. */
export function fromCents(cents: number): string {
  if (!Number.isInteger(cents)) throw new Error('ERR_INVALID_MONEY');
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / FACTOR);
  const fraction = String(abs % FACTOR).padStart(MONEY_SCALE, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

/** Normalise any accepted money input to the canonical wire string. */
export function money(value: MoneyInput): string {
  return fromCents(toCents(value));
}

export function addMoney(...values: MoneyInput[]): string {
  return fromCents(values.reduce<number>((sum, v) => sum + toCents(v), 0));
}

export function subtractMoney(a: MoneyInput, b: MoneyInput): string {
  return fromCents(toCents(a) - toCents(b));
}

/**
 * Multiply an amount by a whole quantity. Quantity is integral by contract — this
 * system sells discrete units of hardware, never 1.5 of a printer — so the product is
 * exact and needs no rounding.
 */
export function multiplyMoney(value: MoneyInput, quantity: number): string {
  if (!Number.isInteger(quantity)) throw new Error('ERR_INVALID_QUANTITY');
  return fromCents(toCents(value) * quantity);
}

/**
 * Apply a percentage (e.g. VAT at `8` or `18`), rounding half-up to the cent.
 *
 * Half-up, not banker's rounding: it is what a Sri Lankan invoice does by hand and what
 * the customer will get if they check the arithmetic on paper.
 */
export function percentOf(value: MoneyInput, percent: number): string {
  if (!Number.isFinite(percent)) throw new Error('ERR_INVALID_PERCENT');
  const cents = toCents(value);
  const raw = (cents * percent) / 100;
  const rounded = raw < 0 ? -Math.round(-raw) : Math.round(raw);
  return fromCents(rounded);
}

export function compareMoney(a: MoneyInput, b: MoneyInput): -1 | 0 | 1 {
  const ca = toCents(a);
  const cb = toCents(b);
  return ca < cb ? -1 : ca > cb ? 1 : 0;
}

export function isZeroMoney(value: MoneyInput): boolean {
  return toCents(value) === 0;
}

/**
 * Present an amount for a human: thousands separators, always two decimals.
 *
 * `currency` is the ISO code shown ahead of the figure (`Rs. 58,500.00`). The symbol
 * table is deliberately tiny — this business quotes in rupees and occasionally in
 * dollars, and an unknown code falls through to the code itself rather than guessing.
 */
const CURRENCY_PREFIX: Record<string, string> = {
  LKR: 'Rs.',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

export function formatMoney(
  value: MoneyInput,
  currency = 'LKR',
  options: { withCurrency?: boolean; decimals?: boolean } = {},
): string {
  const { withCurrency = true, decimals = true } = options;
  const cents = toCents(value);
  const negative = cents < 0;
  const abs = Math.abs(cents);

  const whole = String(Math.floor(abs / FACTOR)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const fraction = String(abs % FACTOR).padStart(MONEY_SCALE, '0');

  const figure = decimals ? `${whole}.${fraction}` : whole;
  const prefix = withCurrency ? `${CURRENCY_PREFIX[currency] ?? currency} ` : '';
  return `${negative ? '-' : ''}${prefix}${figure}`;
}
